import { EditorControl } from '../../common/components/lexical';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { Icon, VStack, Center, Text } from '@cad-arc';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { MultiRowTruncate } from '../../common/components/multi-row-truncate';
import { useMessagingStore } from '../hooks/use-messaging-store';
import { AddMessageForm } from './add-message/add-message-form';
import MessageRenderer from './message-renderer';
import { filteredAndSortedMembers } from '../helpers/conversation-participant-helpers';
import { ViewAllConversationsButton } from './view-all-conversations-button';
import { useApplicationType } from '../../common/hooks/use-application-type';

type MessagesParams = {
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    setShowAllConversations?: (value: boolean) => void;
};

const getDisabledText = ({
    isBookmarksView,
    isSelectedConversationActive,
    sendMessagesAsIndividual,
    isUserConversation,
}: {
    isBookmarksView: boolean;
    isSelectedConversationActive: boolean;
    sendMessagesAsIndividual: boolean;
    isUserConversation: boolean;
}): string | undefined => {
    if (isBookmarksView) {
        return 'Cannot send messages when viewing bookmarks.';
    }
    if (!isSelectedConversationActive) {
        return 'Cannot send messages to inactive conversations.';
    }
    if (isUserConversation && !sendMessagesAsIndividual) {
        return `Do not have 'Send Messages as Individual' ability.`;
    }
    return undefined;
};

export const Messages = observer(
    ({ editorControlRef, setShowAllConversations }: MessagesParams) => {
        const {
            selectedConversation,
            selectedConversationKey,
            isSelectedConversationActive,
            isBookmarksView,
            messagingBootstrapState,
        } = useMessagingStore();
        const { sendMessagesAsIndividual } = useEnabledFeatures();

        const { isFirstResponder } = useApplicationType();

        const members = useMemo(() => {
            return filteredAndSortedMembers(selectedConversation, selectedConversationKey);
        }, [selectedConversation, selectedConversationKey]);

        const ParticipantsList: React.FC = () => {
            return (
                <MultiRowTruncate
                    items={members}
                    maxNumRows={2}
                    expectedCharacterWidth={10}
                    flexWrap="wrap"
                    data-testid="members-list"
                    w="100%"
                    bg="var(--arc-colors-surface-foreground)"
                    p="var(--arc-space-2)"
                    pl="var(--arc-space-3)"
                    pr="var(--arc-space-3)"
                    borderBottom="1px solid var(--arc-colors-border-default)"
                />
            );
        };

        if (messagingBootstrapState?.isError) {
            return null;
        } else if (selectedConversationKey === undefined) {
            return (
                <>
                    {isFirstResponder && (
                        <ViewAllConversationsButton
                            isBookmarksView={isBookmarksView}
                            setShowAllConversations={setShowAllConversations}
                            paddingBottom="var(--arc-space-2)"
                        />
                    )}
                    <Center h="100%" data-testid="no-conversation-selected">
                        <VStack>
                            <Icon size="lg" icon="Messaging" />
                            <Text variant="headingMd">No conversation selected</Text>
                        </VStack>
                    </Center>
                </>
            );
        } else {
            const isUserConversation =
                selectedConversationKey.participantIdentity?.entityType === 'USER';
            const messageInputDisabledText = getDisabledText({
                isBookmarksView,
                isSelectedConversationActive,
                sendMessagesAsIndividual,
                isUserConversation,
            });
            return (
                <>
                    {isFirstResponder && (
                        <ViewAllConversationsButton
                            isBookmarksView={isBookmarksView}
                            setShowAllConversations={setShowAllConversations}
                        />
                    )}
                    <ParticipantsList />
                    <MessageRenderer />
                    <AddMessageForm
                        disabledText={messageInputDisabledText}
                        editorControlRef={editorControlRef}
                    />
                </>
            );
        }
    }
);
